import { HttpStatusCode } from '@angular/common/http';
import { inject, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, forkJoin, NEVER, of, take, tap, timeout } from 'rxjs';

import { CustomersApi } from '@tfs/tool-library-library-api';

import { AuthService } from '../data-access/auth.service';
import { ProfileService } from '../data-access/user.service';

const PROFILE_FETCH_TIMEOUT_MS = 30000;

/**
 * Provides an app initializer that fetches the user profile and subscription on startup.
 *
 * This initializer:
 * - Fetches the user profile and subscription in parallel via forkJoin
 * - Sets profileService state on success
 * - Waits up to 30 seconds for the profile response
 * - Runs once on every app load
 *
 * Error handling:
 * - Profile 401: Prevents bootstrap (waits indefinitely while interceptor redirects to login)
 * - Profile server/network/timeout errors: Navigates to /error and allows bootstrap
 * - Subscription errors: Gracefully ignored (sets subscription to null and continues)
 */
export function provideProfileInitializer() {
  return provideAppInitializer(() => {
    const authService = inject(AuthService);
    const profileService = inject(ProfileService);
    const customersApi = inject(CustomersApi);
    const router = inject(Router);

    const profile$ = authService.getUserProfile().pipe(
      timeout(PROFILE_FETCH_TIMEOUT_MS),
      take(1),
      tap(profile => profileService.setUserProfile(profile)),
      catchError(error => {
        console.error('Failed to load user profile:', error);

        // For 401, DON'T resolve — block indefinitely while the auth interceptor redirects to login
        // authService.login() is expected to be called by an interceptor
        if (error.status === HttpStatusCode.Unauthorized) {
          return NEVER;
        }

        // For other errors, navigate to error page and continue bootstrap
        router.navigate(['/error']);
        return of(null);
      })
    );

    const subscription$ = customersApi.getSubscription().pipe(
      tap(sub => profileService.setSubscription(sub)),
      catchError(error => {
        console.error('[ProfileInitializer] Failed to load subscription:', error);
        profileService.setSubscription(null);
        return of(null);
      })
    );

    return firstValueFrom(forkJoin([profile$, subscription$]));
  });
}
